import { Injectable, inject } from '@angular/core';
import { of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountsSortingService } from '../../../features/accounts/services/accounts-sorting.service';
import { Account, AccountDetailsResponse } from '../../../domain/account.domain';
import { DateFormatterService, DateTimeFormat } from '../../../services/date/date-formatter.service';
import { RestService } from '../../../services/rest/rest.service';
import { AccountsDescriptionService } from '../../../features/accounts/services/accounts-description.service';
import { AccountsRestService } from '../../../features/accounts/services/accounts-rest.service';
import {
  CashTransfer,
  CashTransferAggregationResponseWrapper,
  CashTransferAggregationTable,
  CashTransferValidation,
  CashTransferConfirmation,
  CashTransferAccount,
  RecurringCashTransferStatus,
  AT2FundProfile,
  FundProfile, CashTransferStatusResponse
} from './cash-transfer.domain';

@Injectable()
export class CashTransfersRestService {
  private readonly restService = inject(RestService);
  private readonly accountsRestService = inject(AccountsRestService);
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly accountsSortingService = inject(AccountsSortingService);
  private readonly dateFormatterService = inject(DateFormatterService);

  getFromAccounts(): Observable<Array<Account>> {
    return this.restService
      .getResource('account.transferFrom')
      .get<Array<Account>>()
      .pipe(map((accounts: Array<Account>): Array<Account> => {
        this.accountsDescriptionService.setDescriptions(accounts);
        this.accountsSortingService.sort(accounts);
        return accounts;
      }));
  }

  getAccountCash(accountId: string): Observable<AccountDetailsResponse> {
    return this.accountsRestService
      .getAccountDetails(accountId, ['RT_CASH_DETAILS']);
  }

  getAccountDataTotal(): Observable<CashTransferAggregationTable> {
    const aggregationKey = 'ACCOUNT_MARGIN_DATA_TOTAL_GROUPED_BY_NUMBER';
    return this.restService
      .getResource('portfolio_v2.aggregatedBalances')
      .get<CashTransferAggregationResponseWrapper>({
        params: {
          aggregationType: aggregationKey
        }
      })
      .pipe(map((data: CashTransferAggregationResponseWrapper): CashTransferAggregationTable => data[aggregationKey].result));
  }

  getToAccounts(fromAccountSessionKey: string): Observable<Array<CashTransferAccount>> {
    const parameters = { fromAccountSessionKey };
    return this.restService
      .getResource('account_v2.transferTo')
      .post<typeof parameters, Array<CashTransferAccount>>(parameters)
      .pipe(
        map((accounts: Array<CashTransferAccount>): Array<CashTransferAccount> => {
          this.accountsDescriptionService.setDescriptions(accounts);
          this.accountsSortingService.sort(accounts);
          return accounts;
        }),
        catchError((): Observable<Array<CashTransferAccount>> => of([]))
      );
  }

  validate(transfer: CashTransfer): Observable<CashTransferValidation> {
    const parameters = {
      fromAccountSessionKey: transfer.selectedFromAccount.sessionKey,
      toAccountSessionKey: transfer.selectedToAccount.sessionKey,
      transferAmount: transfer.transferAmount,
      currency: transfer.currency,
      contributionType: transfer.contributionType,
      fromAccountId: transfer.selectedFromAccount.id
    };
    return this.restService
      .getResource('account.validate')
      .post<typeof parameters, CashTransferValidation>(parameters);
  }

  complete(response: CashTransferValidation): Observable<CashTransferConfirmation> {
    const fxTransfer = (response !== undefined && response.conversionRate !== undefined);

    const parameters = {
      transferControlNumber: response.transferControlNumber,
      fxTransfer
    };

    return this.restService
      .getResource('account.complete')
      .post<typeof parameters, CashTransferConfirmation>(parameters);
  }

  confirmTransfer(transfer: CashTransfer): Observable<string> {
    const parameters = {
      fromAccountId: transfer.selectedFromAccount.id,
      toAccountId: transfer.selectedToAccount.id,
      fromAccountSessionKey: transfer.selectedFromAccount.sessionKey,
      toAccountSessionKey: transfer.selectedToAccount.sessionKey,
      transferAmount: transfer.transferAmount,
      frequencyType: transfer.frequencyType,
      contributionType: transfer.contributionType,
      planType: transfer.planType,
      startDate: this.dateFormatterService.formatDate(transfer.startDate, DateTimeFormat.YEAR_MONTH_DAY)
    };

    return this.restService
      .getResource('recurringTransfer.confirmTransfer')
      .post<typeof parameters, string>(parameters);
  }

  getFundProfileAccount(accountId: string): Observable<FundProfile> {
    const parameters = {
      pathVariables: { accountId: decodeURIComponent(accountId) }
    };

    return this.restService.getResource('account.fundProfile')
      .get<Array<FundProfile>>(parameters)
      .pipe(map((data: Array<FundProfile>): FundProfile => data?.length > 0 ? data[0] : undefined));
  }

  // Used to fetch the profile (including cleared buying power) for AT2.0 accounts
  getProfileForAT2Accounts(accountNumber: string): Observable<AT2FundProfile> {
    const params = {
      agreementNum: accountNumber
    };

    return this.restService.getResource('account.activeTraderFundProfile')
      .get<Array<AT2FundProfile>>({ params })
      .pipe(map((data: Array<AT2FundProfile>): AT2FundProfile => data?.length > 0 ? data[0] : undefined));
  }

  getRecurringTransferStatus(): Observable<Array<RecurringCashTransferStatus>> {
    return this.restService
      .getResource('recurringTransfer.ticketStatus')
      .get<Array<RecurringCashTransferStatus>>()
      .pipe(
        map((cashTransferStatuses: Array<RecurringCashTransferStatus>): Array<RecurringCashTransferStatus> => {
          cashTransferStatuses.forEach((transferStatus: RecurringCashTransferStatus): void => {
            this.accountsDescriptionService.setAccountDescription(transferStatus.fromAccount);
            this.accountsDescriptionService.setAccountDescription(transferStatus.toAccount);
          });
          return cashTransferStatuses;
        }));
  }

  getAllRecurringTransferStatus(): Observable<Array<RecurringCashTransferStatus>> {
    // Backend pass this parameter to get both cash and OFI recurring transfers
    const withValidatedFromAccount = false;

    return this.restService
      .getResource('recurringTransfer.ticketStatus')
      .get<Array<RecurringCashTransferStatus>>({
        params: { withValidatedFromAccount }
      })
      .pipe(
        map((cashTransferStatuses: Array<RecurringCashTransferStatus>): Array<RecurringCashTransferStatus> => {
          cashTransferStatuses.forEach((transferStatus: RecurringCashTransferStatus): void => {
            if (transferStatus.fromAccount) {
              this.accountsDescriptionService.setAccountDescription(transferStatus.fromAccount);
            }
            this.accountsDescriptionService.setAccountDescription(transferStatus.toAccount);
          });
          return cashTransferStatuses;
        }));
  }

  /**
   * This method is used for a specific case where the user has selected a Retail/TDCT HISA TFSA
   * as the From account and a DI TFSA as the To account. This type of transfer will require a ticket
   * to be created on the TIK system to be manually processed by the back office.
   * @param transfer The transfer details
   */
  submitTIKTransfer(transfer: CashTransfer): Observable<string> {
    const parameters = {
      fromAccountSessionKey: transfer.selectedFromAccount.sessionKey,
      toAccountId: transfer.selectedToAccount.id,
      toAccountSessionKey: transfer.selectedToAccount.sessionKey,
      transferAmount: transfer.transferAmount,
      currency: transfer.currency
    };
    return this.restService
      .getResource('account.confirm')
      .post<typeof parameters, string>(parameters);
  }

  getStatus(referenceId: string): Observable<CashTransferStatusResponse> {
    const params = { referenceId };
    return this.restService
      .getResource('account.transferStatus')
      .get<CashTransferStatusResponse>({ params });
  }
}

